import { format, parseISO } from 'date-fns';
import { Repeat } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  FREQUENCY_OPTIONS,
  describeFrequency,
  isRecurrenceFrequency,
  nextOccurrences,
  nthOccurrenceDate,
} from '@/lib/recurrence';
import type { RepeatEndMode, RepeatSettings } from './repeat-settings';

interface RepeatSettingsFieldsProps {
  value: RepeatSettings;
  onChange: (next: RepeatSettings) => void;
  /** First visit (yyyy-MM-dd). Drives the preview; empty until a date is picked. */
  firstVisit: string;
  disabled?: boolean;
  idPrefix?: string;
  className?: string;
}

const prettyDate = (d: string) => format(parseISO(d), 'EEE d MMM');

/**
 * "Repeats: Doesn't repeat / Weekly / Fortnightly / Every 4 weeks / Monthly",
 * plus when it ends and a preview of the next few visits.
 */
export function RepeatSettingsFields({ value, onChange, firstVisit, disabled, idPrefix = 'repeat', className }: RepeatSettingsFieldsProps) {
  const set = (patch: Partial<RepeatSettings>) => onChange({ ...value, ...patch });
  const frequency = isRecurrenceFrequency(value.frequency) ? value.frequency : null;

  let preview: string[] = [];
  let lastVisit: string | null = null;
  if (frequency && firstVisit) {
    const endDate =
      value.endMode === 'on_date' && value.endDate
        ? value.endDate
        : value.endMode === 'after' && Number(value.count) >= 1
          ? nthOccurrenceDate(frequency, firstVisit, Number(value.count))
          : null;
    preview = nextOccurrences({ frequency, anchorDate: firstVisit, endDate }, firstVisit, 4);
    lastVisit = endDate;
  }

  return (
    <div className={cn('space-y-3', className)} data-testid="repeat-settings">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-frequency`}>Repeats</Label>
          <Select value={value.frequency} onValueChange={(v) => set({ frequency: v as RepeatSettings['frequency'] })} disabled={disabled}>
            <SelectTrigger id={`${idPrefix}-frequency`} aria-label="Repeats">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Doesn't repeat</SelectItem>
              {FREQUENCY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {frequency && (
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-ends`}>Ends</Label>
            <Select value={value.endMode} onValueChange={(v) => set({ endMode: v as RepeatEndMode })} disabled={disabled}>
              <SelectTrigger id={`${idPrefix}-ends`} aria-label="Ends">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="never">Keeps going</SelectItem>
                <SelectItem value="on_date">On a date</SelectItem>
                <SelectItem value="after">After a number of visits</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {frequency && value.endMode === 'on_date' && (
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-end-date`}>Last visit on or before</Label>
            <Input
              id={`${idPrefix}-end-date`}
              type="date"
              min={firstVisit || undefined}
              value={value.endDate}
              onChange={(e) => set({ endDate: e.target.value })}
              disabled={disabled}
            />
          </div>
        )}
        {frequency && value.endMode === 'after' && (
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-count`}>Number of visits</Label>
            <Input
              id={`${idPrefix}-count`}
              type="number"
              min={2}
              max={520}
              value={value.count}
              onChange={(e) => set({ count: e.target.value })}
              disabled={disabled}
            />
          </div>
        )}
      </div>

      {frequency && (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-[12.5px] text-muted-foreground">
          <Repeat className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
          {firstVisit ? (
            <p data-testid="repeat-preview">
              <span className="font-medium text-foreground">{describeFrequency(frequency, firstVisit)}</span>
              {' · '}
              {preview.map(prettyDate).join(', ')}
              {preview.length === 4 && (!lastVisit || preview[3] < lastVisit) ? '…' : ''}
              {lastVisit ? ` · last visit ${prettyDate(lastVisit)}` : ''}
              <span className="block pt-0.5">
                We'll add visits 12 weeks ahead and keep topping them up. Any that would double-book a cleaner are flagged for you instead.
              </span>
            </p>
          ) : (
            <p>Pick the first visit's date to see the schedule.</p>
          )}
        </div>
      )}
    </div>
  );
}
